import {
  appAbandonToken,
  appRefreshTokenFromTimeoutModal,
} from '@actions/app.action';
import { CommonModule } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { MILLISECONDS_PER_SECOND } from '@constants/app.constants';
import { LetDirective } from '@directives/app-let.directive';
import { ModalTokenTimeoutCode, ModalType } from '@models/app.models';
import { GrowthBookFeatureKeys } from '@models/growthbook.models';
import { Store } from '@ngrx/store';
import { selectTokenExpiresAt } from '@selectors/app.selector';
import { selectAppCmsCopy } from '@selectors/cms.selector';
import { BBoxService } from '@services/bbox/bbox.service';
import { GrowthBookService } from '@services/growthbook/growthbook.service';
import { Duration } from 'luxon';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, Subject, timer } from 'rxjs';
import { map, takeUntil, takeWhile, tap, withLatestFrom } from 'rxjs/operators';

@Component({
  selector: 'app-token-timeout-warning-modal',
  standalone: true,
  imports: [CommonModule, LetDirective],
  templateUrl: './token-timeout-warning-modal.component.html',
  styleUrls: ['./token-timeout-warning-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TokenTimeoutWarningModalComponent implements OnDestroy, OnInit {
  onDestroy$ = new Subject<void>();

  readonly COUNTDOWN_INTERVAL_SECONDS = 1;

  countdown$: Observable<Duration>;
  expired = false;
  appCmsCopy$ = this.store.select(selectAppCmsCopy);
  readonly GrowthBookFeatureKeys = GrowthBookFeatureKeys;

  constructor(
    public bsModalRef: BsModalRef,
    private store: Store,
    public gbs: GrowthBookService,
    private cdr: ChangeDetectorRef,
    private bboxService: BBoxService,
  ) {}

  ngOnInit(): void {
    const msCountdownInterval =
      this.COUNTDOWN_INTERVAL_SECONDS * MILLISECONDS_PER_SECOND;
    this.countdown$ = timer(0, msCountdownInterval).pipe(
      withLatestFrom(this.store.select(selectTokenExpiresAt)),
      map(([, tokenExpiresAt]) =>
        Duration.fromMillis(
          tokenExpiresAt.getTime() - new Date().getTime(),
        ).shiftTo('minutes', 'seconds'),
      ),
      tap(duration => {
        this.expired = duration.as('seconds') <= 0;
        if (this.expired) {

          this.bboxService.trackModal({
            type: ModalType.TOKEN_TIMEOUT,
            code: ModalTokenTimeoutCode.TOKEN_EXPIRED,
          });

          this.cdr.detectChanges();
        }
      }),
      takeWhile(duration => duration.as('seconds') > 0),
      takeUntil(this.onDestroy$),
    );
    this.gbs.onChange$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.cdr.detectChanges();
      });

    this.bboxService.trackModal({
      type: ModalType.TOKEN_TIMEOUT,
      code: ModalTokenTimeoutCode.TOKEN_TIMEOUT_WARNING,
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }

  extendSession(): void {
    this.store.dispatch(appRefreshTokenFromTimeoutModal());
  }

  endSession(): void {
    this.bsModalRef.hide();
    this.store.dispatch(appAbandonToken());
  }

  acceptFate(): void {
    this.bsModalRef.hide();
    // ensure token truly abandoned before page is refreshed
    this.store.dispatch(appAbandonToken());
  }
}
